/**
 * Stale-chunk recovery.
 *
 * After every deploy, Vite renames each code chunk with a new content hash.
 * A browser tab that was opened (or a cached app shell) from BEFORE the deploy
 * still asks for the OLD chunk filename, which no longer exists — producing
 * "Failed to fetch dynamically imported module: /assets/page-xxxx.js".
 *
 * The only correct recovery is a single reload: the fresh HTML points at the
 * fresh chunk names. A short cooldown guard prevents any reload loop if a chunk
 * is genuinely, permanently missing.
 */

const CHUNK_RELOAD_KEY = 'cendien:chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 10000;

const CHUNK_ERROR_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk \d+ failed|dynamically imported module/i;

/** Is this error the stale/missing lazy-chunk load failure? */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === 'string') return CHUNK_ERROR_RE.test(error);
  const message = (error as { message?: string })?.message || '';
  return CHUNK_ERROR_RE.test(message);
}

/**
 * Reload the page once to pick up the latest build.
 * Returns true if a reload was triggered, false if still inside the cooldown
 * window (meaning we already tried recently — don't loop).
 */
export function reloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — still safe to reload a single time.
  }
  window.location.reload();
  return true;
}